import { AuthenticationError, ClientError, HttpError, HttpErrorOptions, ServerError, TimeoutError } from '~/errors';
import { createLogger } from '~/utils/logging';

const log = createLogger('HttpClient');

const DEFAULT_TIMEOUT_MS = 15000; // 15 seconds

type HttpClientConfig = {
  getToken?: () => string | null;
  resolveNewToken?: () => Promise<string>;
  timeoutMs?: number;
};

type RequestOptions = RequestInit & {
  timeoutMs?: number;
};

export class HttpClient {
  private baseUrl: string;
  private config: HttpClientConfig;

  constructor(baseUrl: string, config: HttpClientConfig = {}) {
    this.baseUrl = baseUrl;
    this.config = config;
  }

  get isAuthenticated(): boolean {
    return this.config.getToken?.() != null;
  }

  async delete<TResponse>(url: string, options: RequestOptions = {}): Promise<TResponse> {
    return this.fetch<TResponse>(url, {
      method: 'DELETE',
      ...options,
    });
  }

  async get<TResponse>(url: string, options: RequestOptions = {}): Promise<TResponse> {
    return this.fetch<TResponse>(url, {
      method: 'GET',
      ...options,
    });
  }

  async post<TResponse>(url: string, data: unknown, options: RequestOptions = {}): Promise<TResponse> {
    return this.fetch<TResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put<TResponse>(url: string, data: unknown, options: RequestOptions = {}): Promise<TResponse> {
    return this.fetch<TResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  private async fetch<TResponse>(url: string, options: RequestOptions = {}, _isRetry = false): Promise<TResponse> {
    const timeoutMs = options.timeoutMs ?? this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const { timeoutMs: _, ...requestOptions } = options;

    const token = this.config.getToken?.();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...requestOptions.headers,
    };

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...requestOptions,
        headers,
        signal: controller.signal,
      });
      log.info(`"${options.method} ${url} HTTP/1.1" - ${response.status}`, {
        method: options.method ?? 'UNKNOWN',
        status: response.status,
      });

      if (response.status === 304) {
        return {} as unknown as TResponse;
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error: ${response.status}`;
        let errorCode: string | undefined;
        let detail: unknown;
        let email: string | undefined;
        try {
          const errorData = await response.json();
          detail = errorData.detail;
          errorCode = typeof errorData.errorCode === 'string' ? errorData.errorCode : undefined;
          email = typeof errorData.email === 'string' ? errorData.email : undefined;
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
          // If detail is an array (Pydantic 422), keep the generic message;
          // callers should read error.detail for field-level errors.
        } catch {
          // Ignore JSON parse errors
        }
        const retryAfterRaw = response.headers.get('Retry-After');
        const retryAfter = retryAfterRaw != null ? parseInt(retryAfterRaw, 10) : undefined;

        const errorArgs = Object.fromEntries(
          [
            ['statusCode', response.status],
            ['errorCode', errorCode],
            ['retryAfter', retryAfter],
            ['detail', detail],
            ['email', email],
          ].filter(([, v]) => v !== undefined),
        ) as HttpErrorOptions;

        if (response.status >= 500) {
          log.error(errorMessage);
          throw new ServerError(errorMessage, errorArgs);
        } else if (response.status === 401) {
          // Attempt transparent token refresh on 401 (not 403).
          if (!_isRetry && this.config.resolveNewToken) {
            try {
              await this.config.resolveNewToken();
              return this.fetch<TResponse>(url, options, true);
            } catch {
              // Refresh failed — throw the original 401 as AuthenticationError.
            }
          }
          throw new AuthenticationError(errorMessage, errorArgs);
        } else if (response.status === 403) {
          throw new AuthenticationError(errorMessage, errorArgs);
        } else if (response.status >= 400 && response.status < 500) {
          log.warn(errorMessage);
          throw new ClientError(errorMessage, errorArgs);
        }
        throw new HttpError(errorMessage, errorArgs);
      }
      return response.json();
    } catch (error) {
      if (error instanceof HttpError) {
        // One of our own errors thrown above; just rethrow.
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        // AbortError is thrown by the AbortController when the request is aborted.
        log.error(`"${options.method} ${url} HTTP/1.1" - TIMEOUT`);
        throw new TimeoutError(`Request duration exceeded ${timeoutMs}ms`);
      }
      if (error instanceof Error) {
        // Unknown error; log and wrap it in our own error.
        log.error(`"${options.method} ${url} HTTP/1.1" - ERROR (${error.message})`);
        throw new HttpError(error.message, { statusCode: 500 });
      }
      // Other object thrown as an error (e.g. a string).
      log.error(`"${options.method} ${url} HTTP/1.1" - ERROR (${error})`);
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }
}
